import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiCall } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export default function Chat() {
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [activeConvo, setActiveConvo] = useState(conversationId || '');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => apiCall<any[]>('/chat/conversations'),
  });

  const { data: messages } = useQuery({
    queryKey: ['messages', activeConvo],
    queryFn: () => apiCall<any[]>(`/chat/conversations/${activeConvo}/messages`),
    enabled: !!activeConvo,
    refetchInterval: 5000,
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => apiCall(`/chat/conversations/${activeConvo}/messages`, { method: 'POST', body: JSON.stringify({ content, message_type: 'text' }) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['messages', activeConvo] }); setMessage(''); },
  });

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  return (
    <div className="page" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 'var(--space-md)', height: 'calc(100vh - 120px)' }}>
      {/* Conversation list */}
      <div style={{ borderRight: '1px solid var(--color-tan)', paddingRight: 'var(--space-md)', overflowY: 'auto' }}>
        <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-md)' }}>Conversations</h2>
        {(conversations?.data || []).map((convo: any) => (
          <div key={convo.id} onClick={() => setActiveConvo(convo.id)} className="card" style={{ marginBottom: 8, cursor: 'pointer', border: activeConvo === convo.id ? '2px solid var(--color-brown)' : undefined }}>
            <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>#{convo.service_request_id?.slice(0, 8)}</p>
            <p style={{ fontSize: '0.8rem', opacity: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{convo.last_message || 'No messages yet'}</p>
            {convo.unread_count > 0 && <span className="badge badge-pending">{convo.unread_count} new</span>}
          </div>
        ))}
      </div>

      {/* Message area */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {!activeConvo ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
            <p>Select a conversation</p>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-md)' }}>
              {(messages?.data || []).map((msg: any) => (
                <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender_id === user?.id ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{ maxWidth: '70%', padding: '10px 14px', borderRadius: 'var(--radius-lg)', background: msg.sender_id === user?.id ? 'var(--color-brown)' : 'var(--color-cream)', color: msg.sender_id === user?.id ? 'var(--color-cream)' : 'var(--color-dark-brown)', border: msg.sender_id !== user?.id ? '1px solid var(--color-tan)' : 'none' }}>
                    <p style={{ fontSize: '0.9rem' }}>{msg.content}</p>
                    <p style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: 4 }}>{new Date(msg.created_at * 1000).toLocaleTimeString()}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ display: 'flex', gap: 8, padding: 'var(--space-md)', borderTop: '1px solid var(--color-tan)' }}>
              <input type="text" value={message} onChange={e => setMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && message && sendMutation.mutate(message)} placeholder="Type a message..." style={{ flex: 1, padding: '12px 16px', borderRadius: 'var(--radius-full)', border: '2px solid var(--color-tan)', fontFamily: 'var(--font-family)', background: 'var(--color-cream)' }} />
              <button className="btn-primary" onClick={() => message && sendMutation.mutate(message)} disabled={!message} style={{ borderRadius: 'var(--radius-full)' }}>Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
